import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const MarketFitContext = createContext(null);

export function MarketFitProvider({ children }) {
  const { user } = useAuth();
  const [recordsByProject, setRecordsByProject] = useState({});
  const [limitsByProject, setLimitsByProject] = useState({});
  const [loading, setLoading] = useState(false);

  const refreshRecords = useCallback(async (projectId) => {
    if (!projectId || !user) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/market-fit`);
      setRecordsByProject((prev) => ({ ...prev, [projectId]: data.records || [] }));
      setLimitsByProject((prev) => ({ ...prev, [projectId]: data.limits || {} }));
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createRecord = useCallback(async (projectId, body) => {
    const { data } = await api.post(`/projects/${projectId}/market-fit`, body);
    await refreshRecords(projectId);
    return data;
  }, [refreshRecords]);

  const deleteRecord = useCallback(async (projectId, recordId) => {
    await api.delete(`/projects/${projectId}/market-fit/${recordId}`);
    setRecordsByProject((prev) => ({
      ...prev,
      [projectId]: (prev[projectId] || []).filter((r) => r.id !== recordId),
    }));
  }, []);

  const value = useMemo(() => ({
    recordsByProject,
    limitsByProject,
    loading,
    refreshRecords,
    createRecord,
    deleteRecord,
  }), [recordsByProject, limitsByProject, loading, refreshRecords, createRecord, deleteRecord]);

  return <MarketFitContext.Provider value={value}>{children}</MarketFitContext.Provider>;
}

export function useMarketFit() {
  const ctx = useContext(MarketFitContext);
  if (!ctx) throw new Error("useMarketFit must be inside MarketFitProvider");
  return ctx;
}
